/**
 * What an item proto actually says, in words — the model behind the Inventory
 * tab's hover card.
 *
 * ## Why this is a separate, DOM-free module
 *
 * The catalog already knows every field of every proto (`CatalogItem.fields`,
 * decoded by `mods/proto-codec`). Turning those numbers into "8-16 normal, 2 AP"
 * is a pile of per-subtype special cases, and every one of them is a claim about
 * the engine that can be right or wrong. Keeping it out of the renderer means it
 * is readable next to `engine/src/proto_types.h`, which is the only thing that
 * makes it checkable.
 *
 * ## Sources, all verified rather than remembered
 *
 *   - Field names and wire order: `mods/fields.ts`, transcribed from
 *     `protoRead` / `protoItemDataRead` (engine/src/proto.cc:1563-1692).
 *   - Struct field meanings and enum extents: `engine/src/proto_types.h`
 *     (`ProtoItemWeaponData` &c at :380-464, `ProtoFlags` :305,
 *     `ProtoExtendedFlags` :332).
 *   - The attack-mode nibbles: `extendedFlags & 0xF` is the primary mode and
 *     `(extendedFlags & 0xF0) >> 4` the secondary, indexing the nine modes named
 *     in `_attack_subtype` (engine/src/item.cc:135-144, :1222-1224). This is why
 *     the low byte of `extendedFlags` is NEVER read as booleans here: on a
 *     weapon it is data, not flags.
 *   - Stat ids for drugs: `saveedit/constants.ts` STAT_NAMES, i.e. the Stat
 *     enum. `-2` is the engine's "slot unused" and is skipped.
 *
 * ## What is deliberately not here
 *
 * Nothing is computed that the proto does not state. There is no "effective
 * damage against a target in metal armour", no ammo-vs-weapon cross-reference
 * beyond naming the default ammo's pid: those depend on the wielder and the
 * target, and a hover card that quietly assumes a build is worse than one that
 * reports the file.
 */

import { DAMAGE_TYPES, MATERIAL_TYPES, type ItemTypeName } from '../mods/fields';
import { PERK_NAMES, STAT_NAMES } from './constants';

/**
 * How a player writes the calibers, in `CaliberType` order (proto_types.h:119).
 * `CALIBER_TYPES` in fields.ts spells them the way the *enum* does, because it is
 * a recipe vocabulary and a recipe names fields; a hover card is read by someone
 * who calls it "10mm", not "mm10". Same order, same length — the index is the
 * enum value either way.
 */
const CALIBER_LABELS: readonly string[] = [
  'none', 'rocket', 'flamethrower fuel', 'small energy cell', 'micro fusion cell',
  '.223', '5mm', '.40 cal', '10mm', '.44 cal', '14mm', '12 gauge', '9mm', 'BB',
  '.45 cal', '2mm EC', '4.7mm caseless', 'HN needler', '7.62mm',
];

export interface DetailRow {
  label: string;
  value: string;
  /**
   * A second, dimmer line under the value: what the value *means* when the value
   * alone is a name rather than a number. Currently only the granted perk uses
   * it — "Accurate" is not an answer to "what does this weapon do".
   */
  note?: string;
}

export interface DetailGroup {
  /** Null for the leading unlabelled group. */
  label: string | null;
  rows: DetailRow[];
}

export interface ItemDetail {
  name: string;
  /** "Weapon" / "Armor" — the subtype, title-cased, plus any headline flag. */
  kind: string;
  /** From `pro_item.msg` entry `messageId + 1`. Empty when the install has none. */
  description: string;
  groups: DetailGroup[];
  /** The pid/type/flags line, always present — it is the fallback content. */
  engine: string[];
}

/**
 * What a proto-granted perk actually *does*, traced in `engine/src`.
 *
 * A weapon or a suit of armour names a perk in its `perk` field, and the game
 * never explains it: vanilla `perk.msg` stores the name again in the description
 * slot for every one of these (entry 1101+id is literally "Weapon Penetrate"),
 * because they are engine-internal and the perk screen never shows them. So the
 * name alone — the only thing the card used to show — is not an answer. These
 * lines are.
 *
 * Keyed by the `perk_defs.h` enum name (via `PERK_NAMES`, minus the `PERK_`
 * prefix) rather than by number, so the table stays readable against the header
 * and survives anyone re-checking it.
 *
 * Every line is transcribed from a read of the code, cited beside it. Nothing
 * here is remembered from a wiki. Where the effect is conditional — the player
 * only, party members only — the condition is part of the line, because an
 * explanation that quietly omits "this does nothing for Sulik" is worse than no
 * explanation.
 *
 * Perks NOT in this table (a mod may put any perk id in the field) fall back to
 * the install's own `perk.msg` description, and failing that to the bare name.
 * Inventing a mechanic for an id we have not traced is the one thing this must
 * never do.
 */
const PERK_EFFECTS: Readonly<Record<string, string>> = {
  // combat.cc:4525-4527 — the ranged distance term multiplies PE by 4 rather
  // than the default 2 (combat.cc:4533-4535) before subtracting it from the
  // distance, and the result is scaled by -4 into the to-hit (combat.cc:4571).
  WEAPON_LONG_RANGE: 'Distance costs less accuracy: the range term uses PE × 4 instead of PE × 2.',
  // combat.cc:4608-4610.
  WEAPON_ACCURATE: '+20 to hit.',
  // combat.cc:4725-4728. DR is untouched, and an armour-bypassing critical
  // (combat.cc:4720-4722) already does this, so the two do not stack.
  WEAPON_PENETRATE: "Target's damage threshold cut to 20%. Its damage resistance is unchanged.",
  // combat.cc:4844-4846 — the knockback divisor is 5 instead of 10.
  WEAPON_KNOCKBACK: 'Knocks the target back twice as far: damage ÷ 5 hexes rather than ÷ 10.',
  // combat.cc:4528-4531 sets the multiplier to 5 and a minimum distance of 8;
  // combat.cc:4553-4560 adds the 8 back as a penalty inside that range.
  WEAPON_SCOPE_RANGE: 'PE × 5 range term at 8 hexes or more — but −32 to −60 to hit closer in.',
  // item.cc:1728-1732.
  WEAPON_FAST_RELOAD: 'Reloading costs 1 AP instead of 2.',
  // combat.cc:4633-4646 — the darkness penalty block runs for the dude only,
  // and the perk forces the target's light to 65536, its maximum.
  WEAPON_NIGHT_SIGHT: 'For the player only: the target counts as fully lit, cancelling the darkness penalty of up to −40.',
  // actions.cc:217-220 — both gore thresholds are divided by 3. Damage is
  // untouched; this is the death animation, not the kill.
  WEAPON_FLAMEBOY: 'Gory death animations trigger at a third of the usual damage. Damage itself is unchanged.',
  // combat.cc:3976-3989, called on any hit (combat.cc:4109-4111), plus
  // combat.cc:4332-4334 which sets the flag outright on a critical.
  WEAPON_ENHANCED_KNOCKOUT: "Every hit gets an attacker's-ST − 8 percent chance to knock the target out; a critical always does.",
  // perk.cc:119 (STAT_RADIATION_RESISTANCE +30, stats[STRENGTH] = 3), applied
  // by perkAddEffect perk.cc:577-599 — the stats[] loop runs because maxRank is
  // -1. Armour perks reach only party members: inventory.cc:3157-3167.
  POWERED_ARMOR: '+3 Strength and +30% radiation resistance. Armour perks apply to the player and party only.',
  // perk.cc:120 — radiation resistance only, every stats[] entry 0.
  COMBAT_ARMOR: '+20% radiation resistance. Armour perks apply to the player and party only.',
  // perk.cc:125.
  ARMOR_ADVANCED_I: '+4 Strength and +60% radiation resistance. Armour perks apply to the player and party only.',
  // perk.cc:126.
  ARMOR_ADVANCED_II: '+4 Strength and +75% radiation resistance. Armour perks apply to the player and party only.',
  // perk.cc:129 — no stat modifier, stats[CHARISMA] = 2. No proto in vanilla
  // grants it, but a mod may.
  ARMOR_CHARISMA: '+2 Charisma. Armour perks apply to the player and party only.',
};

/**
 * The traced effect of a perk id, or null when we have not traced it.
 *
 * `PERK_NAMES` is indexed by the `perk_defs.h` enum, so an id past its end is a
 * mod's own perk and gets no line — which is the correct answer, not a gap.
 */
const perkEffect = (id: number): string | null =>
  (id >= 0 ? PERK_EFFECTS[PERK_NAMES[id] ?? ''] : undefined) ?? null;

/** Builds the "Grants" row for a perk id, or null when the field says -1. */
type PerkRow = (label: string, id: number) => DetailRow | null;

/** The nine attack modes the `extendedFlags` nibbles index (item.cc:135-144). */
const ATTACK_MODES = ['none', 'punch', 'kick', 'swing', 'thrust', 'throw', 'single', 'burst', 'continuous'] as const;

/** proto_types.h ProtoExtendedFlags, boolean bits only — see the header note. */
const EXT_FLAGS: ReadonlyArray<readonly [number, string]> = [
  [0x0100, 'big gun'],
  [0x0200, 'two-handed'],
  [0x0800, 'can use'],
  [0x1000, 'can use on'],
  [0x4000, 'can talk to'],
  [0x8000, 'can pick up'],
  [0x08000000, 'hidden (integral, cannot be dropped)'],
];

/** proto_types.h ProtoFlags — the ones that mean something for an item. */
const PROTO_FLAGS: ReadonlyArray<readonly [number, string]> = [
  [0x08, 'flat'],
  [0x10, 'no block'],
  [0x800, 'multihex'],
  [0x1000, 'no highlight'],
  [0x20000000, 'light thru'],
  [0x80000000, 'shoot thru'],
];

const KIND_LABELS: Record<ItemTypeName, string> = {
  armor: 'Armor',
  container: 'Container',
  drug: 'Drug',
  weapon: 'Weapon',
  ammo: 'Ammo',
  misc: 'Misc',
  key: 'Key',
};

const named = (table: readonly string[], index: number): string =>
  table[index] ?? `unknown (${index})`;

/** `0x0000A1B2`, which is how every other Fallout tool spells a fid. */
const hex = (value: number): string => `0x${(value >>> 0).toString(16).padStart(8, '0')}`;

const titleCase = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/** `STRENGTH` -> `Strength`, `MAXIMUM_HIT_POINTS` -> `Maximum hit points`. */
const statLabel = (id: number): string => {
  const key = STAT_NAMES[id];
  if (!key) return `stat ${id}`;
  return titleCase(key.toLowerCase().replaceAll('_', ' '));
};

const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

const bits = (value: number, table: ReadonlyArray<readonly [number, string]>): string[] =>
  table.filter(([bit]) => (value >>> 0 & bit) !== 0).map(([, label]) => label);

/**
 * The rows every item gets. Weight and cost are always shown even at zero: "this
 * item weighs nothing" is a real and load-bearing answer, and a missing row
 * would read as a decoding failure instead.
 */
function commonRows(f: Readonly<Record<string, number>>): DetailRow[] {
  const rows: DetailRow[] = [
    { label: 'Weight', value: `${f.weight ?? 0} lb` },
    { label: 'Value', value: `$${f.cost ?? 0}` },
    { label: 'Inventory size', value: String(f.size ?? 0) },
  ];
  if (f.material !== undefined) rows.push({ label: 'Material', value: named(MATERIAL_TYPES, f.material) });
  if (f.lightDistance) {
    rows.push({ label: 'Light', value: `${f.lightDistance} tiles at intensity ${f.lightIntensity ?? 0}` });
  }
  return rows;
}

function weaponRows(f: Readonly<Record<string, number>>, perkRow: PerkRow): DetailRow[] {
  const rows: DetailRow[] = [];
  const min = f.minDamage ?? 0;
  const max = f.maxDamage ?? 0;
  rows.push({
    label: 'Damage',
    value: `${min}-${max} ${named(DAMAGE_TYPES, f.damageType ?? 0)}`,
  });

  const ext = (f.extendedFlags ?? 0) >>> 0;
  const primary = named(ATTACK_MODES, ext & 0xf);
  const secondary = named(ATTACK_MODES, (ext & 0xf0) >> 4);
  rows.push({
    label: 'Primary',
    value: `${primary} · ${f.actionPointCost1 ?? 0} AP · range ${f.maxRange1 ?? 0}`,
  });
  // A weapon with no secondary mode reads mode 0 in the high nibble, and saying
  // "none, 0 AP, range 0" four times a hover is noise.
  if (((ext & 0xf0) >> 4) !== 0) {
    rows.push({
      label: 'Secondary',
      value: `${secondary} · ${f.actionPointCost2 ?? 0} AP · range ${f.maxRange2 ?? 0}`,
    });
  }

  if (f.rounds) rows.push({ label: 'Burst', value: `${f.rounds} round${f.rounds === 1 ? '' : 's'}` });
  if ((f.caliber ?? 0) !== 0 || (f.ammoCapacity ?? 0) !== 0) {
    const caliber = named(CALIBER_LABELS, f.caliber ?? 0);
    rows.push({
      label: 'Ammo',
      value: `${caliber}${f.ammoCapacity ? ` · holds ${f.ammoCapacity}` : ''}`
        + (f.ammoTypePid !== undefined && f.ammoTypePid >= 0 ? ` · default pid ${f.ammoTypePid & 0xffffff}` : ''),
    });
  }
  rows.push({ label: 'Min ST', value: String(f.minStrength ?? 0) });
  if (f.projectilePid !== undefined && f.projectilePid >= 0) {
    rows.push({ label: 'Projectile', value: `pid ${f.projectilePid & 0xffffff}` });
  }
  const grants = perkRow('Grants', f.perk ?? -1);
  if (grants) rows.push(grants);
  return rows;
}

function armorRows(f: Readonly<Record<string, number>>, perkRow: PerkRow): DetailRow[] {
  const rows: DetailRow[] = [{ label: 'Armor class', value: signed(f.armorClass ?? 0) }];
  // DT and DR are two halves of one answer, so they share a row per damage type.
  // Types the armour does nothing about are dropped: seven rows of zeroes would
  // bury the three that matter.
  for (const type of DAMAGE_TYPES) {
    const dt = f[`damageThreshold.${type}`] ?? 0;
    const dr = f[`damageResistance.${type}`] ?? 0;
    if (!dt && !dr) continue;
    rows.push({ label: titleCase(type), value: `DT ${dt} · DR ${dr}%` });
  }
  if (rows.length === 1) rows.push({ label: 'Protection', value: 'none — every DT and DR is 0' });
  const grants = perkRow('Grants', f.perk ?? -1);
  if (grants) rows.push(grants);
  return rows;
}

function ammoRows(f: Readonly<Record<string, number>>): DetailRow[] {
  const rows: DetailRow[] = [
    { label: 'Caliber', value: named(CALIBER_LABELS, f.caliber ?? 0) },
    { label: 'Rounds per box', value: String(f.quantity ?? 0) },
  ];
  if (f.armorClassModifier) rows.push({ label: "Target's AC", value: signed(f.armorClassModifier) });
  if (f.damageResistanceModifier) rows.push({ label: "Target's DR", value: `${signed(f.damageResistanceModifier)}%` });
  const mult = f.damageMultiplier ?? 1;
  const div = f.damageDivisor ?? 1;
  rows.push({ label: 'Damage', value: `× ${mult} / ${div}` });
  return rows;
}

/**
 * A drug is three (stat, immediate, delayed, delayed) tuples plus addiction.
 * One row per affected stat, because the player's question is "what does this do
 * to my Strength", not "what is in amount1[0]".
 */
function drugRows(f: Readonly<Record<string, number>>, perkRow: PerkRow): DetailRow[] {
  const rows: DetailRow[] = [];
  for (let i = 0; i < 3; i++) {
    const stat = f[`stat.${i}`];
    // -2 is the engine's unused-slot marker; anything negative is not a Stat.
    if (stat === undefined || stat < 0) continue;
    const parts: string[] = [];
    const now = f[`amount.${i}`] ?? 0;
    if (now) parts.push(`${signed(now)} now`);
    const first = f[`amount1.${i}`] ?? 0;
    if (first) parts.push(`${signed(first)} after ${f.duration1 ?? 0} ticks`);
    const second = f[`amount2.${i}`] ?? 0;
    if (second) parts.push(`${signed(second)} after ${f.duration2 ?? 0} ticks`);
    rows.push({ label: statLabel(stat), value: parts.length ? parts.join(', ') : 'no change' });
  }
  if (!rows.length) rows.push({ label: 'Effects', value: 'none this proto declares' });
  rows.push({ label: 'Addiction', value: f.addictionChance ? `${f.addictionChance}%` : 'none' });
  if (f.addictionChance) {
    rows.push({ label: 'Withdrawal', value: `begins after ${f.withdrawalOnset ?? 0} ticks` });
    const grants = perkRow('Withdrawal effect', f.withdrawalEffect ?? -1);
    if (grants) rows.push(grants);
  }
  return rows;
}

function subtypeRows(
  itemType: ItemTypeName,
  f: Readonly<Record<string, number>>,
  perkRow: PerkRow
): DetailRow[] {
  switch (itemType) {
    case 'weapon':
      return weaponRows(f, perkRow);
    case 'armor':
      return armorRows(f, perkRow);
    case 'ammo':
      return ammoRows(f);
    case 'drug':
      return drugRows(f, perkRow);
    case 'container':
      return [
        { label: 'Capacity', value: `${f.maxSize ?? 0} size units` },
        { label: 'Open flags', value: hex(f.openFlags ?? 0) },
      ];
    case 'misc': {
      const rows: DetailRow[] = [];
      if (f.charges) rows.push({ label: 'Charges', value: String(f.charges) });
      if ((f.powerType ?? -1) >= 0) rows.push({ label: 'Consumes', value: named(CALIBER_LABELS, f.powerType!) });
      if ((f.powerTypePid ?? -1) >= 0) rows.push({ label: 'Recharged by', value: `pid ${f.powerTypePid! & 0xffffff}` });
      return rows;
    }
    case 'key':
      return [{ label: 'Key code', value: String(f.keyCode ?? 0) }];
  }
}

export interface ItemDetailSource {
  pid: number;
  name: string;
  description: string;
  itemType: ItemTypeName;
  inventoryFid: number;
  fileName: string;
  fields: Readonly<Record<string, number>>;
}

/**
 * The hover card's content for one item. Pure, cheap and total: a proto whose
 * subtype block is missing fields yields fewer rows, never a throw, because the
 * only thing worse than an incomplete hover is one that breaks the list.
 */
export function itemDetail(
  item: ItemDetailSource,
  opts: {
    perkName?: (id: number) => string | null;
    /** `perk.msg` entry 1101+id, the install's own words. The fallback, not the answer. */
    perkDescription?: (id: number) => string | null;
  } = {}
): ItemDetail {
  const f = item.fields ?? {};
  const perkRow: PerkRow = (label, id) => {
    if (id < 0) return null;
    const name = opts.perkName?.(id) ?? `perk ${id}`;
    // The traced effect first. Failing that, the install's own description —
    // but only when it says something the name did not: vanilla stores the name
    // again in the description slot for every proto-granted perk, and echoing
    // "Weapon Penetrate — Weapon Penetrate" is worse than showing nothing.
    const described = opts.perkDescription?.(id)?.trim();
    const note = perkEffect(id) ?? (described && described !== name ? described : undefined);
    return { label, value: name, ...(note ? { note } : {}) };
  };

  const ext = (f.extendedFlags ?? 0) >>> 0;
  const headline = ext & 0x0200 ? 'two-handed' : ext & 0x0100 ? 'big gun' : '';
  const kind = KIND_LABELS[item.itemType] + (headline ? ` · ${headline}` : '');

  const groups: DetailGroup[] = [];
  const sub = subtypeRows(item.itemType, f, perkRow);
  if (sub.length) groups.push({ label: KIND_LABELS[item.itemType], rows: sub });
  groups.push({ label: 'Item', rows: commonRows(f) });

  const flags = [
    // Weapons spend the low byte on attack modes, so those bits are reported as
    // modes above and never re-spelled as flags here.
    ...bits(item.itemType === 'weapon' ? ext & ~0xff : ext, EXT_FLAGS),
    ...bits(f.flags ?? 0, PROTO_FLAGS),
  ];
  const engine = [
    `pid ${item.pid} · proto ${item.fileName}`,
    `type ${f.type ?? 0} (${item.itemType})`,
    `fid ${hex(f.fid ?? 0)} · inventory fid ${hex(item.inventoryFid)}`,
    `message ${f.messageId ?? 0}`,
    flags.length ? `flags: ${flags.join(', ')}` : 'flags: none set',
  ];

  return { name: item.name, kind, description: item.description, groups, engine };
}
